import workerpool from "workerpool";

import { chunkify, Logger } from "@abacus/common";
import {
  PortHandlerResponse,
  PortReader,
  PortServices,
  withChannel,
} from "@abacus/port-utils";
import { ProgressTracker } from "@/progressTracker";

import env from "@/env";
import { Runner, JobName, ChunkRunResult } from "@/types";
import { getDupes } from "@/utils/utils";
import {
  getDefaultAbacusContractClient,
  getWorkerPool,
} from "@/utils/clientUtils";

import { terminateContractsJobDataSchema } from "./schemas";
import {
  TerminateContractRequest,
  TerminateContractsJobConfig,
  TerminateContractsJobData,
  TerminateContractsJobResult,
} from "./types";
import { OwsAbacusContractClient } from "@abacus/ows-contract-client/fetch";

export class TerminateContractsJob implements Runner {
  private contractClient?: OwsAbacusContractClient;
  readonly logger?: Logger;
  private pool?: workerpool.Pool;

  constructor(config?: TerminateContractsJobConfig) {
    this.logger = config?.logger;
  }

  async run(
    data: TerminateContractsJobData,
  ): Promise<TerminateContractsJobResult> {
    try {
      const { requests } = terminateContractsJobDataSchema.parse(data);
      this.logger?.info(`Total input: ${requests.length}`);

      if (requests.length < 1) {
        return { errors: [], success: 0, total: 0 };
      }

      return await this.terminateContracts(requests);
    } finally {
      await this.terminate();
    }
  }

  private async terminateContracts(
    reqs: TerminateContractRequest[],
  ): Promise<TerminateContractsJobResult> {
    const chunkSize = env.CHUNK_SIZE;
    const errors: [number, string][] = [];
    const onBatchResult = ({
      errors: chunkErrors,
      total: chunkTotal,
    }: ChunkRunResult<void>): PortHandlerResponse<void> => {
      for (const [index, message] of chunkErrors) {
        errors.push([indexes[index], message]);
      }
      tracker.count += chunkTotal;
      tracker.errors = errors.length;
      this.logger?.info(tracker.toString());
      return { value: undefined };
    };

    this.logger?.info(`Pinging API ows-contract-event...`);
    this.contractClient = getDefaultAbacusContractClient();
    const contractClient = this.contractClient;
    const status = (await contractClient.getHealth()).data.status;
    this.logger?.info(`\tAPI status: ${status}`);

    const tracker = new ProgressTracker(reqs.length);
    const [indexes, dupeIndexes] = getDupes(reqs, (req) => req.contractId);
    for (const index of dupeIndexes) {
      errors.push([index, "Skipped duplicate"]);
    }
    tracker.count = errors.length;
    tracker.errors = errors.length;
    reqs = indexes.map((index) => reqs[index]);

    this.logger?.info(`Creating worker pool...`);
    this.pool ??= getWorkerPool(reqs.length);
    this.logger?.info(`\tMax workers: ${this.pool.maxWorkers!}`);
    this.logger?.info(`\tChunk size: ${chunkSize}`);

    this.logger?.info(`Creating services...`);
    const chunks = chunkify(chunkSize, reqs);
    const services = new PortServices();
    services.add("AbacusContractClient", contractClient);
    services.add("Chunks", chunks);

    this.logger?.info("Running workers...");
    const workerPromises: Promise<void>[] = [];
    for (let i = 0; i < this.pool.maxWorkers!; ++i) {
      workerPromises.push(
        withChannel(({ port1, port2 }) => {
          const reader = new PortReader(port1).start();
          services.listenOn(port1);
          reader.on("results", onBatchResult);
          return this.pool!.exec(JobName.TerminateContracts, [port2], {
            transfer: [port2],
          });
        }),
      );
    }

    await Promise.all(workerPromises);

    return {
      errors,
      success: reqs.length - errors.length,
      total: reqs.length,
    };
  }

  private async terminate(): Promise<void> {
    const results = await Promise.allSettled([this.pool?.terminate()]);
    this.pool = undefined;
    for (const result of results) {
      if (result.status === "rejected") {
        throw result.reason;
      }
    }
  }

  async [Symbol.asyncDispose](): Promise<void> {
    this.terminate();
  }
}
